import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ShareIntroducerTick } from "@/components/hub/ShareIntroducerTick";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ukDay, ukDayYear } from "@/lib/time";
import { caseState, gbp, ladderFor, laneStage } from "@/lib/stages";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { STAGE_LABEL, STAGES } from "./constants";
import { CaseThread } from "@/components/portal/crm/CaseThread";
import { DocRequests } from "@/components/portal/DocRequests";
import { CaseFiles } from "@/components/hub/CaseFiles";
import { DealValueCard } from "@/components/hub/DealValue";
import { RehomeDialog, RehomeButton } from "@/components/hub/RehomeDialog";
import { LaneActions, usePullReasons } from "@/components/hub/LanePressure";
import { HubLaneNow } from "@/components/hub/HubLaneNow";
import { UpdateHistory } from "@/components/portal/crm/UpdateHistory";
import { RemoveDialog } from "@/components/hub/RemoveDialog";
import { RoutePicks, AutoRoutingNote } from "@/components/hub/RoutePicks";
import { ClientAsksPanel } from "@/components/hub/ClientAsks";
import { ConnieCaseCard } from "@/components/hub/ConnieCaseCard";
import { I } from "@/components/portal/sys/icons";
import { DocsProgress } from "@/components/portal/crm/DocsProgress";
import { CompanyCheckCard } from "@/components/portal/crm/company/CompanyCheckCard";
import { CompanyCheckReport } from "@/components/portal/crm/company/CompanyCheckReport";
import { useCompanyCheck } from "@/lib/companyCheck";
import { PitchEditor } from "@/components/hub/PitchEditor";
import { PdfLog } from "@/components/hub/PdfLog";
import {
  Btn, CellMain, CellSub, Divider, Empty, Kv, Ladder, Money, PageBody, PageHead, Panel,
  RailCard, Sec, StatusChip, Tabs, Tag, Tb, Td, Th,
} from "@/components/portal/crm/kit";

/* The case record — mockup 03 in the Hub's own voice: the thread first, then the panel,
   the routing door and the paperwork. Everything the desk can do to a case lives here. */



export default function HubLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("thread");
  const [pitchReady, setPitchReady] = useState(false);
  useEffect(() => {
    api.get(`/hub/leads/${id}/pitch/status`).then((r) => setPitchReady(r.data.status === "draft" && r.data.auto)).catch(() => {});
  }, [id, tab]);
  const [selected, setSelected] = useState([]);
  const [share, setShare] = useState([]);
  const [routeNotes, setRouteNotes] = useState("");
  const [routing, setRouting] = useState(false);
  const [pingAnim, setPingAnim] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [threadKey, setThreadKey] = useState(0);
  const pullReasons = usePullReasons();
  const cc = useCompanyCheck(id, "admin");
  const openReport = () => { setTab("company"); document.getElementById("hub-case-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }); };

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get(`/hub/leads/${id}`); setData(r.data); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const commissionPreview = useMemo(() => {
    const c = data?.referrer_commission;
    if (!c) return null;
    return { label: c.label, short: c.short, rate: c.rate, model: c.model, onCompleted: c.on_completed_gbp };
  }, [data]);

  const toggle = (lid) => setSelected((s) => s.includes(lid) ? s.filter((x) => x !== lid) : [...s, lid]);

  const availableMatched = (data?.matched || []).filter((m) => !m.already_sent);

  const doRoute = async () => {
    if (!selected.length) { toast.error("Pick at least one desk"); return; }
    setRouting(true);
    const targets = availableMatched.filter((m) => selected.includes(m.id)).map((m) => ({ id: m.id, name: m.company_name }));
    setPingAnim({ lead_name: data?.lead?.applicant_name || "Applicant", lenders: targets });
    try {
      const r = await api.post(`/hub/leads/${id}/route`, { lender_ids: selected, notes: routeNotes || null,
        share_introducer_ids: share.filter((x) => selected.includes(x)) });
      toast.success(`Sent to ${selected.length} desk${selected.length === 1 ? "" : "s"}`);
      (r.data?.warnings || []).forEach((w) => toast.warning(w, { duration: 7000 }));
      setSelected([]); setShare([]); setRouteNotes("");
      setTimeout(() => { setPingAnim(null); load(); }, 2200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Route failed");
      setPingAnim(null);
    }
    finally { setRouting(false); }
  };

  const setStage = async (v) => {
    try { await api.patch(`/hub/leads/${id}/stage`, { stage: v }); toast.success("Stage updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const togglePaid = async (lender_id) => {
    try { await api.patch(`/hub/leads/${id}/routes/${lender_id}/paid`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const toggleArchive = async () => {
    const on = !!data?.lead?.archived;
    setArchiving(true);
    try {
      await api.post(`/hub/leads/${id}/${on ? "unarchive" : "archive"}`, on ? {} : { reason: "Archived by hand" });
      toast.success(on ? "Back in the live book" : "Archived — off the desk, still findable under Archived");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setArchiving(false); }
  };

  if (loading) return <Empty testId="hub-lead-loading">Opening the case…</Empty>;
  if (!data) {
    return (
      <Panel testId="hub-lead-missing">
        <Empty>
          That case isn&apos;t in the book.
          <div style={{ marginTop: 16 }}><Btn as={Link} to="/hub/leads">Back to the book</Btn></div>
        </Empty>
      </Panel>
    );
  }

  const { lead, matched, unmatched, referrer } = data;
  const files = lead.files || [];
  const routes = lead.routes || [];
  const live = routes.filter((r) => r.status !== "declined");
  const leadState = caseState(lead);

  const rail = (
    <>
      <DealValueCard leadId={id} watch={lead.updated_at} />

      <RailCard title="The applicant" testId="hub-lead-contact">
        <Kv rows={[
          ["Name", lead.applicant_name],
          ["Email", <span style={{ fontSize: 12.5 }} key="e">{lead.applicant_email || "—"}</span>],
          ["Phone", lead.sms_undeliverable
            ? <span key="p" style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end" }}>{lead.applicant_phone}
                <span className="attn-pill" data-testid="hub-lead-phone-undelivered"><i className="attn-dot" />Texts not landing — check the number with the client</span></span>
            : lead.applicant_phone || "—"],
          ["Opened", ukDayYear(lead.created_at, "—")],
        ]} />
      </RailCard>

      {referrer && !lead.direct && (
        <RailCard title="Introduced by" testId="hub-lead-introducer">
          <CellMain style={{ fontSize: 14.5 }}>{referrer.name}</CellMain>
          <CellSub>{referrer.email}{referrer.company ? ` · ${referrer.company}` : ""}</CellSub>
          {lead.submitted_by_name && (
            <CellSub style={{ marginTop: 4 }} data-testid="hub-lead-submitted-by">
              Put in by <b style={{ color: "var(--ink)" }}>{lead.submitted_by_name}</b> · a seat on their portal
            </CellSub>
          )}
          {commissionPreview && (
            <>
              <Divider style={{ margin: "16px 0" }} />
              <div data-testid="lead-referrer-commission">
                <div className="rail-t" style={{ marginBottom: 7 }}>
                  {commissionPreview.rate} {commissionPreview.model === "commission_share" ? "of our fee"
                    : commissionPreview.model === "revenue_share" ? "of funded" : "per deal"}
                </div>
                {commissionPreview.onCompleted != null
                  ? <Money v={commissionPreview.onCompleted} size="lg" sub="Earned on completion" />
                  : <CellSub style={{ margin: 0 }}>Paid on completion — see the breakdown above.</CellSub>}
              </div>
            </>
          )}
        </RailCard>
      )}

      <RailCard title={lead.archived ? "This case is archived" : "Archive this case"} testId="hub-lead-archive-card">
        <CellSub style={{ margin: "0 0 14px" }}>
          {lead.archived
            ? `${lead.archived_reason || "Out of the live book"} — hidden from the desk, the pipeline, the fee book and both portals. Restoring it puts it straight back.`
            : "For a deal that is finished with. It drops off the desk, the pipeline, the fee book and both partner portals, but stays findable under Archived. Cases where every desk passed, nothing has moved for 7 days and 3 nudges have gone out are archived automatically."}
        </CellSub>
        <button className="btn btn-ghost btn-block" onClick={toggleArchive} disabled={archiving}
                data-testid={lead.archived ? "hub-lead-unarchive" : "hub-lead-archive"}>
          {archiving ? "Working…" : lead.archived ? "Restore it to the live book" : "Archive the case"}
        </button>
      </RailCard>

      <RailCard title="Remove this case" testId="hub-lead-remove-card">
        <CellSub style={{ margin: "0 0 14px" }}>
          It leaves the Hub and both partner portals at once, with its documents, notes and
          requests — and waits in the Bin for 30 days.
        </CellSub>
        <button className="btn btn-ghost btn-block" onClick={() => setRemoving(true)} data-testid="hub-lead-remove"
                style={{ color: "var(--ink-3)" }}>
          Delete the case
        </button>
      </RailCard>
    </>
  );

  return (
    <div data-testid="hub-lead-detail">
      {pingAnim && <PingtreeOverlay leadName={pingAnim.lead_name} lenders={pingAnim.lenders} />}

      <button onClick={() => nav("/hub/leads")} data-testid="hub-lead-back"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, border: 0, background: "none", cursor: "pointer",
                font: '700 12.5px "Instrument Sans"', color: "var(--mut)", padding: 0, marginBottom: 14, whiteSpace: "nowrap" }}>
        <I.back /> The whole book
      </button>

      <PageHead
        eyebrow={lead.reference ? `Case ${lead.reference}` : "Case record"}
        title={lead.business_name || lead.applicant_name}
        testId="hub-lead-head"
        meta={
          <>
            <StatusChip state={leadState} testId="hub-lead-stage-pill" />
            {leadState.key === "awaiting_docs" && <DocsProgress progress={lead.docs_progress} variant="inline" testId="hub-lead-docs-progress" />}
            <span style={{ color: "var(--mut)", fontSize: 13 }}>{lead.finance_type} · {gbp(lead.amount_gbp)}</span>
            <span style={{ width: 1, height: 16, background: "var(--line-2)" }} />
            <span style={{ color: "var(--mut)", fontSize: 13 }}>
              {lead.direct ? "Direct · website application" : `${lead.source_type}${lead.source_name ? ` · ${lead.source_name}` : ""}`}
            </span>
          </>
        }
        actions={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span className="rail-t" style={{ marginBottom: 0 }}>Stage</span>
            <Select value={lead.stage} onValueChange={setStage}>
              <SelectTrigger className="w-44 h-10" data-testid="hub-lead-stage"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      <PageBody rail={rail}>
        <Panel pad={false} testId="hub-lead-ladder">
          <div className="card-b">
            <Ladder steps={ladderFor(lead.stage, leadState)} />
            <Divider />
            <div className="case-figures" data-testid="hub-lead-strip">
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Facility sought</div>
                <div className="money" style={{ fontSize: 22 }}>{gbp(lead.amount_gbp)}</div>
              </div>
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Desks holding it</div>
                <div className="money" style={{ fontSize: 22 }}>{live.length} of {routes.length}</div>
                <div className="money-sub">{routes.length - live.length} passed</div>
              </div>
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Documents on file</div>
                <div className="money" style={{ fontSize: 22 }}>{files.length}</div>
              </div>
              <div>
                <div className="rail-t" style={{ marginBottom: 7 }}>Last movement</div>
                <div className="money" style={{ fontSize: 22 }}>{ukDay(lead.updated_at || lead.created_at, "—")}</div>
              </div>
            </div>
          </div>
        </Panel>

        <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          {lead.archived && (
            <div className="warn" data-testid="hub-lead-archived-banner">
              <span>
                <b>Archived{lead.archived_at ? ` on ${ukDay(lead.archived_at, "")}` : ""}.</b>{" "}
                {lead.archived_reason || "Out of the live book"} — it is hidden from the desk, the pipeline,
                the fee book and both partner portals. Restore it from the panel on the right and everything
                comes back exactly as it was.
              </span>
            </div>
          )}
          <NeedsNewHomeBanner lead={lead} matched={matched} onDone={load} />
          <CustomerReviewBanner lead={lead} onDone={load} />
          <ApiCaseBanner lead={lead} onRoute={() => setTab("routing")} />
        </div>

        <div style={{ marginTop: 22 }}>
          <CompanyCheckCard cc={cc} role="admin" onOpen={openReport} testId="hub-company-check" />
        </div>

        <div style={{ marginTop: 22 }}>
          <UpdateHistory lanes={routes.map((r) => ({ name: r.company_name, updates: r.updates }))} aud="hub" testId="hub-case-updates" />
        </div>

        <div style={{ marginTop: 34, scrollMarginTop: 80 }} id="hub-case-tabs">
          <Tabs
            value={tab}
            onChange={setTab}
            testPrefix="hub-case-tab"
            items={[
              { key: "thread", label: "Thread" },
              { key: "routing", label: "Routing", count: routes.length },
              { key: "overview", label: "The business" },
              { key: "company", label: "Company check" },
              { key: "pitch", label: pitchReady ? "Deal pitch · ready" : "Deal pitch" },
              { key: "documents", label: "Documents", count: files.length },
            ]}
          />
        </div>

        {tab === "thread" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <ConnieCaseCard leadId={id} onChanged={() => setThreadKey((k) => k + 1)} />
            </div>
            <Panel
              title="Everything on this case, in order"
              sub="Notes, partner messages, documents and every decision — one feed, masked per audience. Drag a document anywhere onto it."
              testId="hub-case-thread"
            >
              <CaseThread key={threadKey} leadId={id} testPrefix="hub-thread" />
            </Panel>
            <Sec>
              <ClientAsksPanel leadId={id} onChanged={load} />
            </Sec>
          </>
        )}

        {tab === "routing" && (
          <>
            <Panel pad={false} testId="hub-lead-routes"
                   title={`On the panel · ${routes.length}`}
                   sub="Terms are snapshotted onto each route when it is sent, so a later rate change can't re-price history.">
              {routes.length === 0 ? (
                <Empty testId="hub-lead-routes-empty">Not with a desk yet — pick desks below.</Empty>
              ) : (
                <Tb head={<><Th>Desk</Th><Th>Status</Th><Th>Sent</Th><Th className="r">Their terms</Th><Th className="r">What they said</Th><Th className="r">Next move</Th></>}>
                  {routes.map((r) => (
                    <tr key={r.lender_id} data-testid={`hub-route-history-${r.lender_id}`}>
                      <Td className="lead">
                        <CellMain>{r.company_name}</CellMain>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 7 }}>
                          {r.auto_routed && <Tag>Auto</Tag>}
                          {r.over_cap && <span data-testid={`hub-route-over-cap-${r.lender_id}`}><Tag amber>Over cap</Tag></span>}
                          {r.sent_while_paused && <span data-testid={`hub-route-paused-${r.lender_id}`}><Tag amber>Sent while paused</Tag></span>}
                        </div>
                      </Td>
                      <Td>
                        <StatusChip state={laneStage(r)} testId={`hub-route-stage-${r.lender_id}`} />
                        <HubLaneNow leadId={id} route={r} onDone={load} />
                      </Td>
                      <Td><div className="date">{ukDay(r.sent_at, "—")}</div></Td>
                      <Td className="r">
                        <Money raw={r.commission_pct != null ? `${r.commission_pct}%` : "—"} />
                        <CellSub>Of their income</CellSub>
                      </Td>
                      <Td className="r">
                        {r.status === "declined" ? (
                          <CellSub style={{ margin: 0 }} >
                            <span data-testid={`hub-route-decline-reason-${r.lender_id}`}>
                              {r.withdrawn
                                ? `We took it off them — ${(r.withdrawn_reason || "no reason recorded").toLowerCase()}`
                                : (r.decline_reason || "No reason given")}
                            </span>
                          </CellSub>
                        ) : r.lender_notes ? (
                          <CellSub style={{ margin: 0 }}>&ldquo;{r.lender_notes}&rdquo;</CellSub>
                        ) : r.status === "completed" && referrer ? (
                          <button className="btn btn-ghost btn-sm" onClick={() => togglePaid(r.lender_id)}
                                  data-testid={`hub-route-paid-${r.lender_id}`}
                                  style={r.paid ? { borderColor: "var(--s-off-bd)", background: "var(--s-off-bg)", color: "var(--s-off-tx)" } : undefined}>
                            {r.paid ? "Commission paid" : "Mark commission paid"}
                          </button>
                        ) : <CellSub style={{ margin: 0 }}>Nothing yet</CellSub>}
                      </Td>
                      <Td className="r">
                        <LaneActions leadId={id} route={r} reasons={pullReasons} onDone={load} caseLabel={lead.business_name || lead.applicant_name} />
                      </Td>
                    </tr>
                  ))}
                </Tb>
              )}
            </Panel>

            <Sec title="Send it to another desk"
                 sub="Matched on finance type and deal band, then fit-scored. A paused or over-cap desk is shown but never pre-picked.">
              <Panel testId="hub-lead-routing">
                {availableMatched.length === 0 && (unmatched || []).length === 0 ? (
                  <CellSub style={{ margin: 0 }}>No desks configured yet — add some under Lenders.</CellSub>
                ) : (
                  <>
                    <CellSub style={{ margin: "0 0 14px" }}>
                      {matched.length} desk{matched.length === 1 ? "" : "s"} match this case
                      {availableMatched.length !== matched.length ? ` · ${matched.length - availableMatched.length} already has it` : ""}.
                    </CellSub>
                    <AutoRoutingNote testId="hub-route-auto-note" />
                    <RoutePicks options={availableMatched} selected={selected} onChange={setSelected}
                                testPrefix="hub-route-picks" />
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                      {availableMatched.map((m) => (
                        <label key={m.id} className={`opt${selected.includes(m.id) ? " pick" : ""}`}
                               style={{ display: "flex", alignItems: "flex-start", gap: 13, cursor: "pointer" }}
                               data-testid={`hub-route-option-${m.id}`}>
                          <input type="checkbox" checked={selected.includes(m.id)} onChange={() => toggle(m.id)}
                                 style={{ accentColor: "var(--accent)", width: 15, height: 15, marginTop: 3 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <CellMain>{m.company_name}</CellMain>
                            <CellSub>
                              {m.company_type} · {m.delivery_method} · {m.commission_type === "flat"
                                ? `${gbp(m.commission_flat_gbp)} flat` : `${m.commission_pct}% commission`}
                              {m.turnaround_days != null ? ` · ${m.turnaround_days}d turnaround` : ""}
                            </CellSub>
                            {referrer && m.referrer_commission_gbp != null && (
                              <CellSub style={{ marginTop: 3 }}>
                                <span data-testid={`route-referrer-earn-${m.id}`}>
                                  {referrer.name.split(" ")[0]} earns {gbp(m.referrer_commission_gbp)} if it funds here
                                </span>
                              </CellSub>
                            )}
                            {selected.includes(m.id) && !lead.direct && (
                              <ShareIntroducerTick first={(referrer?.name || "").split(" ")[0]} on={share.includes(m.id)}
                                                   onToggle={() => setShare((s) => s.includes(m.id) ? s.filter((x) => x !== m.id) : [...s, m.id])}
                                                   testId={`hub-route-share-introducer-${m.id}`} />
                            )}
                            <CapacityNote cap={m.capacity} id={m.id} />
                          </div>
                          <FitBadge fit={m.fit} />
                        </label>
                      ))}
                      {(unmatched || []).map((m) => (
                        <div key={m.id} className="opt" style={{ display: "flex", alignItems: "center", gap: 13, opacity: .55 }}>
                          <input type="checkbox" disabled style={{ width: 15, height: 15 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <CellMain>{m.company_name}</CellMain>
                            <CellSub>{m.company_type} · {m.commission_pct}% commission</CellSub>
                          </div>
                          <CellSub style={{ margin: 0 }}>{m.reason}</CellSub>
                        </div>
                      ))}
                    </div>
                    {availableMatched.length > 0 && (
                      <div style={{ marginTop: 18 }}>
                        <textarea className="inp" value={routeNotes} onChange={(e) => setRouteNotes(e.target.value)}
                                  placeholder="Anything the desks should know before they open it…"
                                  data-testid="hub-route-notes" />
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                          <Btn kind="primary" onClick={doRoute} disabled={routing || selected.length === 0} testId="hub-route-submit">
                            {routing ? "Sending…" : `Send to ${selected.length || 0} desk${selected.length === 1 ? "" : "s"}`}
                          </Btn>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </Panel>
            </Sec>
          </>
        )}

        {tab === "overview" && (
          <>
            <div className="grid2">
              <Panel title="The applicant" testId="hub-lead-applicant">
                <Kv rows={[
                  ["Business", lead.business_name || "—"],
                  ["Company number", lead.company_number || "—"],
                  ["Sector", lead.industry_sector || "—"],
                  ["Finance type", lead.finance_type],
                  ["Amount", gbp(lead.amount_gbp)],
                  ["Term", lead.term_months ? `${lead.term_months} mo` : "—"],
                  ["Urgency", (lead.urgency || "—").replace(/_/g, " ")],
                ]} />
              </Panel>
              <Panel title="Financials" testId="hub-lead-financials">
                <Kv rows={[
                  ["Annual revenue", lead.annual_revenue_gbp ? gbp(lead.annual_revenue_gbp) : "—"],
                  ["Monthly revenue", lead.monthly_revenue_gbp ? gbp(lead.monthly_revenue_gbp) : "—"],
                  ["Net profit", lead.net_profit_gbp != null ? gbp(lead.net_profit_gbp) : "—"],
                  ["Employees", lead.employees != null ? String(lead.employees) : "—"],
                  ["Existing debt", lead.existing_debt === true ? "Yes" : lead.existing_debt === false ? "No" : "Unknown"],
                  ["PG available", lead.personal_guarantee_available === true ? "Yes" : lead.personal_guarantee_available === false ? "No" : "Unknown"],
                ]} />
              </Panel>
            </div>

            {(lead.loan_purpose || lead.security_offered || lead.existing_debt_summary || lead.notes) && (
              <Sec title="How it was packaged" sub="In the introducer's own words.">
                <Panel testId="hub-lead-deal">
                  <Block label="Loan purpose" value={lead.loan_purpose} />
                  <Block label="Security offered" value={lead.security_offered} />
                  <Block label="Existing debt summary" value={lead.existing_debt_summary} />
                  <Block label="Notes on the case" value={lead.notes} />
                </Panel>
              </Sec>
            )}
          </>
        )}

        {tab === "company" && (
          <>
            <CompanyCheckReport cc={cc} testId="hub-company-report" />
            <div style={{ height: 16 }} />
            <PdfLog leadId={id} doc="company_check" testId="hub-cc-log" />
          </>
        )}

        {tab === "pitch" && (
          <>
            <PitchEditor leadId={id} company={lead.business_name} reference={lead.reference} onStatus={setPitchReady} />
            <div style={{ height: 16 }} />
            <PdfLog leadId={id} doc="deal_pack" testId="hub-pack-log" />
          </>
        )}

        {tab === "documents" && (
          <>
            <Panel title={`Documents on file · ${files.length}`}
                   sub="The original application, client uploads against a request, and anything dropped on the thread."
                   testId="hub-lead-documents">
              <CaseFiles files={files} testPrefix="hub-case-file" />
            </Panel>

            <Sec title="Ask the client for something"
                 sub="They get a private upload link by text and email. The file lands straight on this case and on the thread.">
              <Panel testId="hub-lead-docreqs">
                <DocRequests leadId={id} role="admin" testPrefix="hub-docreq" />
              </Panel>
            </Sec>
          </>
        )}
      </PageBody>

      <RemoveDialog kind="case" id={id} name={lead.business_name || lead.applicant_name}
                    open={removing} onOpenChange={setRemoving}
                    onDeleted={() => nav("/hub/leads")} />
    </div>
  );
}

const Block = ({ label, value }) => {
  if (!value) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div className="rail-t" style={{ marginBottom: 7 }}>{label}</div>
      <p style={{ margin: 0, whiteSpace: "pre-wrap", borderLeft: "2px solid var(--faded)", paddingLeft: 13,
        fontSize: 13.5, lineHeight: 1.65, color: "var(--ink-3)" }}>{value}</p>
    </div>
  );
};

/** CapacityNote — a quiet line when a desk is paused or full, so sending is a choice not an accident. */
function CapacityNote({ cap, id }) {
  if (!cap || (!cap.paused && !cap.at_capacity)) return null;
  return (
    <CellSub style={{ marginTop: 4 }}>
      <span data-testid={`route-capacity-note-${id}`}>
        {cap.paused
          ? "Desk paused — you can still send, they'll see it when they're back"
          : `Over their cap (${cap.used}/${cap.cap_count} ${cap.period_label}) — sending flags it for review`}
      </span>
    </CellSub>
  );
}

/**
 * NeedsNewHomeBanner — a declined case used to go grey and drop out of the book with nothing
 * saying what happened or what to do. This puts the desks, their reasons and the next move at
 * the top of the case.
 */
function NeedsNewHomeBanner({ lead, matched = [], onDone }) {
  const [rehoming, setRehoming] = useState(false);
  if (lead.stage !== "declined") return null;
  const passed = (lead.routes || []).filter((r) => r.status === "declined");
  const available = matched.filter((m) => !m.already_sent).length;
  return (
    <div className="mgr" data-testid="lead-needs-new-home">
      <div className="lab">Needs a new home</div>
      <div className="who">
        {passed.length
          ? `${passed.length} desk${passed.length === 1 ? "" : "s"} passed on this`
          : "This case is marked declined"}
      </div>
      {passed.length > 0 && (
        <div style={{ marginTop: 14 }} data-testid="lead-passed-list">
          {passed.map((r) => (
            <div key={r.lender_id}
                 style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "7px 0",
                   borderTop: "1px solid rgba(255,255,255,.1)", fontSize: 12.5 }}>
              <span style={{ minWidth: 0, color: "rgba(255,255,255,.9)" }}>{r.company_name || "A desk"}</span>
              <span style={{ flex: "none", color: "rgba(255,255,255,.55)", textAlign: "right" }}>
                {r.decline_reason || "no reason given"}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className="role" style={{ marginTop: 14 }}>
        Nothing has been said to the applicant — that call is yours.
        {available
          ? ` ${available} matching desk${available === 1 ? " hasn't" : "s haven't"} seen it yet.`
          : " Every matching desk has already seen it — widen a desk's criteria or add a new one."}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 11, marginTop: 19 }}>
        <RehomeButton onClick={() => setRehoming(true)} />
        <button onClick={() => document.querySelector("[data-testid='hub-case-tab-routing']")?.click()}
                data-testid="lead-rehome-manual"
                style={{ border: 0, background: "none", cursor: "pointer", color: "rgba(255,255,255,.6)",
                  font: '700 12.5px "Instrument Sans"', textDecoration: "underline", textUnderlineOffset: 4 }}>
          Choose desks myself
        </button>
      </div>
      <RehomeDialog leadId={lead.id} open={rehoming} onOpenChange={setRehoming}
                    matched={matched} onDone={onDone} />
    </div>
  );
}

/**
 * CustomerReviewBanner — a deal the customer filled in themselves. Nothing goes to a
 * lender until an admin lets it through, so the decision lives on the deal itself.
 */
/** An introducer's own system sent this in. It is never auto-routed; a repeat client is flagged, not refused. */
function ApiCaseBanner({ lead, onRoute }) {
  if (lead.submitted_via !== "api") return null;
  const fresh = (lead.stage || "new") === "new" && !(lead.routes || []).length;
  return (
    <div className={fresh ? "warn" : "note"} style={{ display: "block" }} data-testid="hub-lead-api-banner">
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>
        Sent in by API{lead.source_name ? <> · {lead.source_name}</> : null}
        {lead.api_external_id ? <span style={{ fontWeight: 500 }}> · their id {lead.api_external_id}</span> : null}
      </div>
      <p style={{ margin: "7px 0 0", fontSize: 12.5, lineHeight: 1.6 }}>
        {fresh ? "API cases are never auto-routed — pick the desks for it on the Routing tab. " : "Placed by hand. "}
        Every stage move goes back to the introducer's system automatically.
      </p>
      {lead.duplicate_of && (
        <p style={{ margin: "10px 0 0", fontSize: 12.5 }} data-testid="hub-lead-api-duplicate">
          <b>Possible duplicate</b> — same {(lead.duplicate_reason || "email").replace("+", " and ")} as an earlier case
          {lead.duplicate_scope === "other" ? " from a different partner" : " from this introducer"}. Accepted, so check before placing it.{" "}
          <Link to={`/hub/leads/${lead.duplicate_of}`} style={{ fontWeight: 700, textDecoration: "underline" }}>Open the earlier case</Link>.
        </p>
      )}
      {fresh && <div style={{ marginTop: 12 }}><Btn sm kind="primary" onClick={onRoute} testId="hub-lead-api-route">Route it</Btn></div>}
    </div>
  );
}

function CustomerReviewBanner({ lead, onDone }) {
  const [busy, setBusy] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  if (!lead?.customer_submitted) return null;

  const pending = lead.review_status === "pending";
  const viaLabel = lead.direct ? "the business funding page"
    : lead.submitted_via === "customer_invite" ? "a tracked invite" : "their introducer's link or QR code";

  const act = async (what) => {
    setBusy(true);
    try {
      if (what === "approve") {
        const r = await api.post(`/hub/customer-applications/${lead.id}/approve`);
        toast.success(r.data?.routed
          ? `Approved — sent to ${r.data.routes_added} desk${r.data.routes_added === 1 ? "" : "s"}`
          : "Approved — no auto-route rule matched, pick desks under Routing");
      } else {
        await api.post(`/hub/customer-applications/${lead.id}/reject`, { reason: reason.trim() || null });
        toast.success("Rejected — the introducer has been told");
      }
      onDone?.();
      window.dispatchEvent(new Event("customer-apps:changed"));
    } catch (e) { toast.error(e?.response?.data?.detail || "That didn't work"); }
    finally { setBusy(false); }
  };

  if (!pending) {
    return (
      <Panel testId="lead-customer-reviewed">
        <CellSub style={{ margin: 0 }}>
          <b style={{ color: "var(--ink)" }}>Completed by the customer</b> through {viaLabel}
          {lead.direct ? <> · <b style={{ color: "var(--ink)" }}>no introducer</b>, it came straight to us</>
            : lead.source_name ? <> · introduced by <b style={{ color: "var(--ink)" }}>{lead.source_name}</b></> : null}.{" "}
          {lead.review_status === "approved" ? "Approved for routing." : `Not proceeding${lead.review_note ? ` — ${lead.review_note}` : ""}.`}
        </CellSub>
      </Panel>
    );
  }

  return (
    <div className="warn" style={{ display: "block" }} data-testid="lead-needs-review">
      <div style={{ fontWeight: 700, fontSize: 13.5 }}>Needs your review before any desk sees it</div>
      <p style={{ margin: "7px 0 0", fontSize: 12.5, lineHeight: 1.6 }}>
        Came in through {viaLabel}
        {lead.direct ? <> · <b>no introducer</b>, straight to us</>
          : lead.source_name ? <> · introduced by <b>{lead.source_name}</b></> : null}. The customer typed this in
        themselves and ticked consent for their details to be shared with lenders.
      </p>
      {lead.duplicate_of && (
        <p style={{ margin: "10px 0 0", fontSize: 12.5 }}>
          <b>Possible duplicate</b> — the same introducer submitted this customer in the last 30 days.{" "}
          <Link to={`/hub/leads/${lead.duplicate_of}`} style={{ fontWeight: 700, textDecoration: "underline" }}>
            Open the earlier case
          </Link>.
        </p>
      )}
      {rejecting ? (
        <div style={{ marginTop: 14 }}>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} className="min-h-[70px] bg-white"
                    placeholder="Why isn't it proceeding? This exact wording goes to the introducer."
                    data-testid="lead-review-reason" />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 9, marginTop: 11 }}>
            <Btn sm onClick={() => setRejecting(false)} testId="lead-review-reject-cancel">Back</Btn>
            <Btn sm onClick={() => act("reject")} disabled={busy} testId="lead-review-reject-confirm"
                 style={{ color: "var(--ink-3)" }}>
              {busy ? "Sending…" : "Reject & tell them"}
            </Btn>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 9, flexWrap: "wrap", marginTop: 14 }}>
          <Btn kind="primary" sm onClick={() => act("approve")} disabled={busy} testId="lead-review-approve">
            {busy ? "Approving…" : "Approve & route"}
          </Btn>
          <Btn sm onClick={() => setRejecting(true)} testId="lead-review-reject"
               style={{ color: "var(--ink-3)" }}>
            Not proceeding
          </Btn>
        </div>
      )}
    </div>
  );
}

/** FitBadge — the lender's 0-100 fit score with a colour band and the reason breakdown. */
function FitBadge({ fit }) {
  const [open, setOpen] = useState(false);
  if (!fit) return <span className="st st-off"><i className="sq" />Match</span>;
  const band = fit.band || "possible";
  const tone = { excellent: "st-fund", good: "st-off", possible: "st-rev", weak: "st-neutral" }[band];
  const label = { excellent: "Excellent", good: "Good fit", possible: "Possible", weak: "Weak" }[band];
  return (
    <div style={{ position: "relative", flex: "none" }} onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <span className={`st ${tone}`} data-testid={`hub-fit-badge-${fit.score}`} style={{ cursor: "help" }}>
        <i className="sq" />{fit.score}% {label}
      </span>
      {open && (
        <div className="menu" style={{ right: 0, top: "calc(100% + 6px)", width: 232 }} data-testid="hub-fit-tooltip">
          <div className="rail-t" style={{ padding: "4px 10px 8px" }}>Fit breakdown · {fit.score}/100</div>
          {(fit.reasons || []).map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "4px 10px", fontSize: 12 }}>
              <span style={{ color: "var(--mut)" }}>{r.label}</span>
              <span className="num" style={{ fontWeight: 700 }}>{r.delta > 0 ? "+" : ""}{r.delta}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * PingtreeOverlay — 2s dark orbital overlay showing the lead node at centre
 * radiating out to each selected lender as they receive the ping.
 */
function PingtreeOverlay({ leadName, lenders }) {
  const size = 320;
  const cx = size / 2;
  const cy = size / 2;
  const radius = 118;
  const nodes = lenders.map((l, i) => {
    const angle = (i / Math.max(1, lenders.length)) * 2 * Math.PI - Math.PI / 2;
    return {
      ...l,
      x: cx + radius * Math.cos(angle),
      y: cy + radius * Math.sin(angle),
      delay: 0.15 + i * 0.12,
    };
  });
  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 120, background: "rgba(5,10,20,.84)",
      backdropFilter: "blur(3px)", display: "grid", placeItems: "center" }} data-testid="hub-pingtree-overlay">
      <style>{`
        @keyframes ct-orbit  { from { stroke-dashoffset: 0; } to { stroke-dashoffset: -60; } }
        @keyframes ct-pulse  { 0%, 100% { opacity: 0.6; transform: scale(1); } 50% { opacity: 1; transform: scale(1.06); } }
        .ct-ring { animation: ct-orbit 4s linear infinite; }
        .ct-hub  { animation: ct-pulse 1.4s ease-in-out infinite; transform-origin: center; }
      `}</style>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#fff" }}>
        <div className="eyebrow" style={{ color: "#7da9ff" }}>Routing live</div>
        <div style={{ font: '700 17px Archivo', marginTop: 6 }}>
          Pinging {lenders.length} desk{lenders.length === 1 ? "" : "s"}…
        </div>

        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow: "visible", marginTop: 24 }}>
          <defs>
            <radialGradient id="ct-hub-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#7da9ff" stopOpacity="0.55" />
              <stop offset="70%" stopColor="#0055FF" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#0055FF" stopOpacity="0" />
            </radialGradient>
          </defs>

          <circle cx={cx} cy={cy} r={radius} fill="none" stroke="rgba(125,169,255,0.16)" strokeWidth="1" strokeDasharray="4 6" className="ct-ring" />
          <circle cx={cx} cy={cy} r={radius - 32} fill="none" stroke="rgba(125,169,255,0.10)" strokeWidth="1" strokeDasharray="2 5" />
          <circle cx={cx} cy={cy} r={radius + 26} fill="none" stroke="rgba(125,169,255,0.06)" strokeWidth="1" strokeDasharray="2 5" />

          {nodes.map((n) => (
            <circle key={`pkt-${n.id}`} r="4" fill="#7da9ff">
              <animate attributeName="cx" from={cx} to={n.x} dur="1.1s" begin={`${n.delay}s`} fill="freeze" />
              <animate attributeName="cy" from={cy} to={n.y} dur="1.1s" begin={`${n.delay}s`} fill="freeze" />
              <animate attributeName="opacity" from="1" to="0" dur="0.25s" begin={`${n.delay + 1.05}s`} fill="freeze" />
            </circle>
          ))}

          {nodes.map((n) => (
            <g key={`node-${n.id}`} opacity="0">
              <animate attributeName="opacity" from="0" to="1" dur="0.2s" begin={`${n.delay + 1.0}s`} fill="freeze" />
              <circle cx={n.x} cy={n.y} r="18" fill="#0055FF" fillOpacity="0.12" stroke="#7da9ff" strokeWidth="1">
                <animate attributeName="r" from="18" to="26" dur="0.9s" begin={`${n.delay + 1.0}s`} fill="freeze" />
                <animate attributeName="fill-opacity" from="0.12" to="0" dur="0.9s" begin={`${n.delay + 1.0}s`} fill="freeze" />
              </circle>
              <circle cx={n.x} cy={n.y} r="10" fill="#0055FF" stroke="#7da9ff" strokeWidth="1" />
              <text x={n.x} y={n.y + 34} fill="#e5edff" textAnchor="middle" fontSize="10" fontFamily="Instrument Sans, ui-sans-serif" fontWeight="600">
                {n.name.length > 14 ? `${n.name.slice(0, 13)}…` : n.name}
              </text>
            </g>
          ))}

          <circle cx={cx} cy={cy} r="56" fill="url(#ct-hub-grad)" />
          <g className="ct-hub">
            <circle cx={cx} cy={cy} r="26" fill="#0B1220" stroke="#7da9ff" strokeWidth="1" />
            <text x={cx} y={cy + 4} fill="#7da9ff" fontSize="10" textAnchor="middle" fontFamily="Instrument Sans, ui-sans-serif" fontWeight="700" letterSpacing="0.14em">CASE</text>
          </g>
          <text x={cx} y={cy + 76} fill="#e5edff" textAnchor="middle" fontSize="11" fontFamily="Instrument Sans, ui-sans-serif" fontWeight="600">
            {leadName.length > 22 ? `${leadName.slice(0, 21)}…` : leadName}
          </text>
        </svg>
      </div>
    </div>
  );
}
